import PageHeader from "../components/shell/PageHeader";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useIsDesktop from "../hooks/useIsDesktop";
import {
  Search,
  PlusCircle,
  Trash2,
  Save,
  X,
  Lightbulb,
  MessageCircle,
  Edit2,
  BookOpen,
  Heart,
  SortAsc,
  SortDesc,
  XCircle,
  TriangleAlert,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Layers,
  HelpCircle,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";

import { createPortal } from "react-dom";
import { usePageNav, usePageNavShell } from "../components/shell/PageNav";
import {
  Lightning,
  TrendUp,
  Clock as PClock,
  Buildings,
  Stack,
  Star as PStar,
  StarHalf as PStarHalf,
} from "@phosphor-icons/react";

// === API ===
import {
  getToken,
  apiGetStrategies,
  apiSaveStrategy,
  apiDeleteStrategy,
  apiToggleFavorite,
  apiStrategyAIReview,
  apiGetStrategyReview,
} from "../api/api";

// === Constantes ===
const MARKETS = ["Forex", "Crypto", "Indices boursiers", "Indices synthétiques", "Actions", "Commodities"];
const TIMEFRAMES = ["M1", "M2", "M3", "M4", "M5", "M10", "M15", "M30", "H1", "H2", "H4", "H8", "D1", "W1", "MN"];
const STRAT_TYPES = ["Scalping", "Intraday", "Swing", "Position", "Automatisée"];
const BREAK_EVEN = ["Oui", "Non", "Trailing Stop"];
const MM_TYPES = ["Risque fixe", "Martingale", "Anti-Martingale", "Semi-fixe", "Tiers", "Kelly", "Intérêt composé"];

// Définitions affichées au survol pour Type de MM
const MM_DEFS = {
  "Risque fixe":
    "Même % de risque par trade (ex : 1%). Taille ajustée pour garder le risque constant.",
  Martingale:
    "Augmenter la taille après une perte pour récupérer plus vite. Très risqué (danger de ruine).",
  "Anti-Martingale":
    "Augmenter après un gain, réduire après une perte. Vise à amplifier les séries gagnantes.",
  "Semi-fixe":
    "Risque ajusté par paliers (ex : palier de capital). Compromis entre souplesse et simplicité.",
  Tiers:
    "Capital divisé en 2/3/… pour répartir le risque. Souvent utilisé pour paniers/échelles.",
  Kelly:
    "Formule théorique maximisant la croissance (nécessite un edge estimé). Très agressif si non borné.",
  "Intérêt composé":
    "Le risque (en %) s’applique sur l’équité actuelle, ce qui fait croître la taille au fil du temps.",
  "": "Survole une option pour voir sa définition.",
};

// === Utilitaires ===
const cx = (...c) => c.filter(Boolean).join(" ");
const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const clamp = (v, min = 0, max = 100) => Math.max(min, Math.min(max, v));
const toStars5 = (score100) => Math.round(((clamp(score100) / 20)) * 2) / 2; // demi-étoiles
const scoreFromReview = (s) => {
  const r = s?.last_review || s?.__review;
  return typeof r?.score_overall === "number" ? r.score_overall : 0;
};

// === Design system “GLACE” ===
const UI = {
  // panels / cards
  panel:
    "rounded-2xl border border-white/30 dark:border-white/12 " +
    "bg-white/55 dark:bg-[#0f172a] backdrop-blur-2xl " +
    " dark: " +
    "relative overflow-hidden",
  panelInset:
    " dark:",
  panelGlow:
    "bg-[radial-gradient(900px_360px_at_10%_0%,rgba(124,58,237,0.16),transparent_55%)," +
    "radial-gradient(700px_320px_at_95%_15%,rgba(14,165,233,0.14),transparent_55%)," +
    "radial-gradient(850px_380px_at_70%_120%,rgba(217,70,239,0.10),transparent_55%)]",

  card:
    "rounded-2xl border border-slate-200/80 dark:border-slate-700/80 " +
    "bg-white/82 dark:bg-[#0f172a] backdrop-blur-2xl " +
    " dark: " +
    "relative overflow-hidden",

  sticky:
    "backdrop-blur-2xl bg-white/80 dark:bg-[#0b1220] border-b border-slate-200/80 dark:border-slate-700/80",

  // inputs
  input:
    "w-full px-3 py-2 rounded-xl text-sm outline-none " +
    "border border-slate-200/90 dark:border-slate-700/90 " +
    "bg-white dark:bg-[#0b1220] backdrop-blur-xl " +
    "text-gray-900 dark:text-slate-100 placeholder:text-gray-500 dark:placeholder:text-slate-400 " +
    "focus:ring-2 focus:ring-violet-500/50 focus:border-violet-400/60 dark:focus:border-violet-400",

  popover:
    "rounded-2xl border border-slate-200/90 dark:border-slate-700/90 " +
    "bg-white/95 dark:bg-[#0b1220] backdrop-blur-2xl " +
    " overflow-hidden",

  tooltip:
    "rounded-lg border border-white/25 dark:border-white/12 " +
    "bg-white/80 dark:bg-[#0b1020]/80 backdrop-blur-2xl " +
    "text-gray-700 dark:text-white ",

  modal:
    "rounded-2xl border border-white/25 dark:border-white/12 " +
    "bg-white/75 dark:bg-[#0b1020] backdrop-blur-2xl " +
    "",

  chip:
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs " +
    "border border-violet-200 dark:border-violet-400/30 bg-violet-50 dark:bg-[#1b1633] backdrop-blur-xl",
};

// === Icône par type (Phosphor) ===
const getStrategyTypeIcon = (type, size = 18) => {
  const common = { size, weight: "duotone" };
  switch (type) {
    case "Scalping": return <Lightning {...common} />;
    case "Intraday": return <TrendUp {...common} />;
    case "Swing": return <PClock {...common} />;
    case "Position": return <Buildings {...common} />;
    case "Automatisée": return <Stack {...common} />;
    default: return <Stack {...common} />;
  }
};

// === Étoiles (Phosphor) ===
function Stars({ score = 0, size = 16 }) {
  const { t } = useTranslation();
  const full = Math.floor(score);
  const half = score % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={t("strategies.stars.ariaLabel", { score })}>
      {Array.from({ length: full }).map((_, i) => (
        <PStar key={`f-${i}`} size={size} weight="fill" className="text-amber-400" />
      ))}
      {half && <PStarHalf size={size} weight="fill" className="text-amber-400" />}
      {Array.from({ length: empty }).map((_, i) => (
        <PStar key={`e-${i}`} size={size} weight="regular" className="text-gray-300 dark:text-gray-600" />
      ))}
    </span>
  );
}

// Palette par type pour l'avatar / progress bar
const TYPE_THEMES = {
  Scalping: { from: "from-pink-500", to: "to-rose-500", ring: "ring-rose-300/40" },
  Intraday: { from: "from-sky-500", to: "to-indigo-500", ring: "ring-sky-300/40" },
  Swing: { from: "from-violet-500", to: "to-purple-500", ring: "ring-violet-300/40" },
  Position: { from: "from-emerald-500", to: "to-teal-500", ring: "ring-emerald-300/40" },
  Automatisée: { from: "from-amber-500", to: "to-orange-500", ring: "ring-amber-300/40" },
  default: { from: "from-slate-500", to: "to-slate-700", ring: "ring-slate-300/40" },
};
const getTypeTheme = (t = "") => TYPE_THEMES[t] || TYPE_THEMES.default;

// === Modèle vide ===
const emptyStrategy = {
  id: undefined,
  is_favorite: false,
  name: "",
  type: "",
  markets: [],
  favorite_assets: "",
  timeframes: [],
  entries: "",
  exits: "",
  exceptions: "",
  take_profit: "",
  stop_loss: "",
  break_even: "",
  mm_type: "",
  mm_explain: "",
  risk_per_trade: null,
  __review: null,
};

/* ===========================
 *  
 * =========================== */
function StrategiesHero() {
  const { t } = useTranslation();
  return (
    <div className="relative mb-6">
      <div className="pointer-events-none absolute inset-x-0 -top-10 -z-10 h-[250px] bg-[radial-gradient(700px_220px_at_12%_18%,rgba(139,92,246,0.20),transparent_60%),radial-gradient(650px_220px_at_90%_20%,rgba(14,165,233,0.16),transparent_58%),radial-gradient(700px_260px_at_50%_100%,rgba(217,70,239,0.10),transparent_60%)]" />

      <div
        className={cx(
          "relative isolate overflow-hidden rounded-[28px] border p-4 md:p-5 xl:p-6",
          "border-white/40 dark:border-white/12",
          "bg-[linear-gradient(135deg,rgba(255,255,255,0.88)_0%,rgba(248,245,255,0.78)_42%,rgba(240,247,255,0.72)_100%)]",
          "dark:bg-[linear-gradient(135deg,rgba(10,16,32,0.96)_0%,rgba(15,20,40,0.92)_42%,rgba(10,24,39,0.92)_100%)]",
          "backdrop-blur-2xl  dark:"
        )}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(1100px_380px_at_-8%_-18%,rgba(147,51,234,0.20),transparent_60%),radial-gradient(780px_300px_at_110%_112%,rgba(59,130,246,0.16),transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.16),transparent)]" />
        <div className="pointer-events-none absolute inset-0 opacity-40 bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0.22)_12%,transparent_24%),radial-gradient(circle_at_18%_30%,rgba(255,255,255,0.65)_0_2px,transparent_3px),radial-gradient(circle_at_80%_18%,rgba(255,255,255,0.65)_0_2px,transparent_3px),radial-gradient(circle_at_72%_74%,rgba(255,255,255,0.50)_0_2px,transparent_3px)]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-[linear-gradient(180deg,transparent,rgba(124,58,237,0.10))]" />

        <div className="relative z-10 grid items-center gap-5 xl:grid-cols-[minmax(0,1.08fr),minmax(380px,0.92fr)]">
          {/* En-tête commun à toutes les pages (PageHeader). */}
          <PageHeader
            className="max-w-3xl"
            icon={Layers}
            title={t("strategies.hero.title")}
            subtitle={
              <>
                {t("strategies.hero.descBefore")}{" "}
                <span className="font-semibold text-slate-950 dark:text-white">
                  {t("strategies.hero.descEmphasis")}
                </span>{" "}
                {t("strategies.hero.descAfter")}
              </>
            }
          />

          <div className="relative hidden min-h-[220px] xl:block">
            <div className="absolute inset-x-8 bottom-0 top-8 rounded-[30px] bg-[radial-gradient(500px_220px_at_50%_50%,rgba(124,58,237,0.20),transparent_70%)] blur-2xl" />
            <div className="absolute left-0 top-6 w-[170px] rounded-[22px] border border-white/45 dark:border-white/12 bg-white/70 dark:bg-[#101827] p-4 backdrop-blur-2xl  dark:">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                {t("strategies.hero.setupQuality")}
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-200/80 dark:bg-white/10 overflow-hidden">
                <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-2 rounded-full bg-slate-200/70 dark:bg-white/10" />
                <div className="h-2 w-4/5 rounded-full bg-slate-200/60 dark:bg-white/10" />
                <div className="h-2 w-3/5 rounded-full bg-slate-200/50 dark:bg-white/10" />
              </div>
            </div>

            <div className="absolute left-[142px] top-0 right-14 rounded-[32px] border border-white/50 dark:border-white/12 bg-white/80 dark:bg-[#101827] p-5 backdrop-blur-2xl  dark:">
              <div className="flex items-center justify-between">
                <div className="h-3 w-20 rounded-full bg-slate-200/80 dark:bg-white/10" />
                <div className="h-3 w-10 rounded-full bg-violet-200/80 dark:bg-violet-500/20" />
              </div>
              <div className="mt-3 grid h-[138px] grid-cols-8 items-end gap-2 rounded-[24px] border border-slate-200/70 dark:border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(247,245,255,0.82))] dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01))] p-4">
                {[
                  [50, 'violet'], [76, 'emerald'], [60, 'violet'], [88, 'violet'],
                  [72, 'emerald'], [102, 'violet'], [86, 'emerald'], [120, 'violet'],
                ].map(([h, tone], idx) => (
                  <div key={idx} className="relative flex h-full items-end justify-center">
                    <div className={cx(
                      "w-4 rounded-full ",
                      tone === 'emerald'
                        ? "bg-gradient-to-b from-emerald-300 to-emerald-500"
                        : "bg-gradient-to-b from-violet-300 to-violet-600"
                    )} style={{ height: `${h}px` }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute bottom-4 right-0 w-[168px] rounded-[22px] border border-white/45 dark:border-white/12 bg-white/75 dark:bg-[#101827] p-4 backdrop-blur-2xl  dark:">
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="grid h-5 w-5 place-items-center rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-200">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div className="h-2 flex-1 rounded-full bg-slate-200/75 dark:bg-white/10" />
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute bottom-6 left-[96px] grid h-10 w-10 place-items-center rounded-2xl border border-white/50 dark:border-white/12 bg-white/80 dark:bg-[#101827] text-violet-700 dark:text-violet-200  backdrop-blur-2xl">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroPill({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-white/45 dark:border-white/12 bg-white/60 dark:bg-[#0f172a] px-2.5 py-1 text-[11px] font-medium text-slate-700 dark:text-white backdrop-blur-xl ">
      {icon ? <span className="text-violet-700 dark:text-violet-200">{icon}</span> : null}
      <span>{label}</span>
    </div>
  );
}

function HeroMetric({ value, label }) {
  return (
    <div className="rounded-xl border border-white/45 dark:border-white/12 bg-white/58 dark:bg-[#0f172a] px-3 py-2.5 backdrop-blur-2xl ">
      <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">{value}</div>
      <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-100">{label}</div>
    </div>
  );
}

// ===================
//   PAGE PRINCIPALE
// ===================
export default function StrategiesPage() {
  const { t } = useTranslation();
  const [userStrats, setUserStrats] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [libOpen, setLibOpen] = useState(true);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const isDesktop = useIsDesktop();
  const { open: pageNavOpen, bodyEl: pageNavBody } = usePageNavShell();
  usePageNav({ title: t("strategies.nav.pageTitle"), buttonLabel: t("strategies.nav.buttonLabel"), buttonIcon: Layers });

  // La liste ne peut être ouverte que si connecté (pas d'erreur de chargement).
  // Sur mobile elle est toujours dépliée : elle occupe la pleine largeur dans le
  // flux, et le rail vertical replié n'y existe pas.
  const listOpen = isDesktop ? libOpen && !fetchError : true;

  // Erreur de chargement (ex: pas connecté) : pop-up.
  useEffect(() => {
    if (fetchError) setShowErrorPopup(true);
  }, [fetchError]);

  // Exclusion mutuelle avec la nav principale : déplier la liste plie la nav.
  // Desktop uniquement — sur mobile la nav est un drawer, elle ne prend aucune
  // largeur, et y toucher écraserait la préférence desktop de l'utilisateur.
  // Au montage on ne plie rien : la nav principale doit rester dépliée à chaque
  // actualisation, on ne la plie qu'après une action de l'utilisateur.
  const navExclusionArmed = useRef(false);
  const listOpenAtMount = useRef(listOpen);
  useEffect(() => {
    if (!navExclusionArmed.current) {
      // Tant que l'état n'a pas bougé depuis le montage, on ne fait rien
      // (compatible double-montage StrictMode en dev).
      if (listOpen === listOpenAtMount.current) return;
      navExclusionArmed.current = true;
    }
    if (isDesktop && listOpen) {
      try { localStorage.setItem("fm:sidebar-collapsed", "true"); } catch {}
      window.dispatchEvent(new Event("fm:sidebar-toggle"));
    }
  }, [isDesktop, listOpen]);

  // Si la nav se déplie, on plie la liste.
  useEffect(() => {
    if (!isDesktop) return undefined;
    const onNavToggle = () => {
      let collapsed = true;
      try { collapsed = localStorage.getItem("fm:sidebar-collapsed") !== "false"; } catch {}
      if (!collapsed) setLibOpen(false);
    };
    window.addEventListener("fm:sidebar-toggle", onNavToggle);
    return () => window.removeEventListener("fm:sidebar-toggle", onNavToggle);
  }, [isDesktop]);

  //
  const loadStrategies = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const token = getToken();
    if (!token) {
      setUserStrats([]);
      setFavoriteIds([]);
      setFetchError(t("strategies.errors.notLoggedIn"));
      setLoading(false);
      return;
    }
    try {
      const res = await apiGetStrategies();
      const list = Array.isArray(res) ? res : [];
      const hydrated = await Promise.all(
        list.map(async (it) => {
          let review = it.last_review || null;
          if (!review) {
            try {
              const r = await apiGetStrategyReview(it.id);
              review = r?.review ?? null;
            } catch (_) { /* silencieux */ }
          }
          return { ...it, __review: review };
        })
      );
      setUserStrats(hydrated);
      setFavoriteIds(hydrated.filter((s) => s.is_favorite).map((s) => s.id));
    } catch (e) {
      console.error(e);
      setFetchError(t("strategies.errors.loadFailed"));
      setUserStrats([]);
      setFavoriteIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);

  // Enregistrer
  const handleSave = useCallback(async (strategy) => {
    if (saving) return;
    setSaving(true);
    setLoading(true);
    setFetchError(null);

    const token = getToken();
    if (!token) {
      setFetchError(t("strategies.errors.authSave"));
      setLoading(false);
      setSaving(false);
      return;
    }

    try {
      const saved = await apiSaveStrategy(strategy, token);
      if (saved?.error) {
        setFetchError(saved.error);
      } else {
        await loadStrategies();
        if (saved?.id) setSelectedId(saved.id);
        setEditing(null);
      }
    } catch (e) {
      console.error("save failed:", e);
      setFetchError(
        typeof e?.message === "string" && e.message.startsWith("HTTP ")
          ? t("strategies.errors.saveFailed")
          : (e?.message || t("strategies.errors.saveFailed"))
      );
    } finally {
      setLoading(false);
      setSaving(false);
    }
  }, [loadStrategies, saving]);

  // Suppression
  const askDelete = useCallback((id) => { setToDelete(id); setShowDelete(true); }, []);
  const cancelDelete = useCallback(() => { setToDelete(null); setShowDelete(false); }, []);
  const confirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setLoading(true);
    setFetchError(null);
    const token = getToken();
    if (!token) {
      setFetchError(t("strategies.errors.authDelete"));
      setLoading(false);
      return;
    }
    try {
      const res = await apiDeleteStrategy(toDelete);
      if (res?.error) setFetchError(res.error);
      else {
        await loadStrategies();
        setSelectedId((prev) => (prev === toDelete ? null : prev));
        setEditing((prev) => (prev?.id === toDelete ? null : prev));
      }
    } catch (e) {
      console.error(e);
      setFetchError(t("strategies.errors.deleteFailed"));
    } finally {
      setToDelete(null);
      setShowDelete(false);
      setLoading(false);
    }
  }, [toDelete, loadStrategies]);

  // Favoris
  const toggleFavorite = useCallback(async (id) => {
    try {
      const res = await apiToggleFavorite?.(id);
      if (res?.error || !res?.id) throw new Error(res?.error || "toggleFavorite failed");
      setUserStrats((prev) => prev.map((s) => (s.id === id ? { ...res, __review: s.__review } : s)));
      setFavoriteIds((prev) => res.is_favorite ? [...new Set([...prev, res.id])] : prev.filter((x) => x !== res.id));
    } catch {
      const curr = userStrats.find((x) => x.id === id);
      if (!curr) return;
      const updated = { ...curr, is_favorite: !curr.is_favorite };
      try {
        const saved = await apiSaveStrategy(updated, getToken());
        setUserStrats((prev) => prev.map((s) => (s.id === id ? { ...saved, __review: curr.__review } : s)));
        setFavoriteIds((prev) => saved.is_favorite ? [...new Set([...prev, saved.id])] : prev.filter((x) => x !== saved.id));
      } catch (e) {
        console.error(e);
        setFetchError(t("strategies.errors.favoriteFailed"));
      }
    }
  }, [userStrats]);

  // Tri / filtrage
  const withStars = useMemo(
    () => userStrats.map((s) => ({ ...s, __stars: toStars5(scoreFromReview(s)) })),
    [userStrats]
  );

  const sorted = useMemo(() => {
    const arr = [...withStars].sort((a, b) => {
      const aFav = favoriteIds.includes(a.id);
      const bFav = favoriteIds.includes(b.id);
      if (aFav !== bFav) return aFav ? -1 : 1;
      let cmp = 0;
      if (sortBy === "name") cmp = (a.name || "").localeCompare(b.name || "");
      else if (sortBy === "type") cmp = (a.type || "").localeCompare(b.type || "");
      else if (sortBy === "score") cmp = (a.__stars || 0) - (b.__stars || 0);
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [withStars, favoriteIds, sortBy, sortOrder]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((s) => {
      const inName = (s.name || "").toLowerCase().includes(q);
      const inType = (s.type || "").toLowerCase().includes(q);
      const inMarkets = (Array.isArray(s.markets) ? s.markets.join(" ") : "").toLowerCase().includes(q);
      const inAssets = (s.favorite_assets || "").toLowerCase().includes(q);
      return inName || inType || inMarkets || inAssets;
    });
  }, [sorted, searchTerm]);

  const selected = useMemo(() => userStrats.find((s) => s.id === selectedId), [userStrats, selectedId]);
  const isViewerFullscreen = Boolean(selected && !editing);

  // Sidebar liste — desktop : colonne collante pleine hauteur, pliable.
  // Mobile : la liste passe en pleine largeur dans le flux (une colonne
  // de 320px, ou son rail vertical replié, n'a pas sa place sur 360px).
  const listPanel = (
    <aside
      className={cx(
        UI.panel, UI.panelInset,
        "shrink-0 overflow-hidden rounded-none border-l-0 border-y-0",
        "w-full p-4",
        "lg:sticky lg:top-0 lg:h-[100dvh] lg:transition-[width] lg:duration-300 lg:ease-in-out",
        listOpen ? "lg:w-[320px] lg:p-5" : "lg:w-14 lg:p-2"
      )}
    >
      <div className={cx("pointer-events-none absolute inset-0", UI.panelGlow)} />
      {fetchError && (
        <div className="pointer-events-none absolute inset-0 animate-pulse bg-rose-500/10" />
      )}
      <div className="relative flex h-full flex-col">
        {listOpen ? (
          <>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-white">{t("strategies.nav.pageTitle")}</div>
              <button
                onClick={() => setLibOpen(false)}
                title={t("strategies.list.collapse")}
                className={cx(
                  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition",
                  "border border-white/40 dark:border-white/12",
                  "bg-white/65 dark:bg-[#0f172a] backdrop-blur-2xl",
                  "text-slate-600 hover:bg-white/80 dark:text-white dark:hover:bg-slate-800"
                )}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto pr-1">
              {loading ? (
                <ListSkeleton />
              ) : fetchError ? (
                <button
                  onClick={() => setShowErrorPopup(true)}
                  className={cx(
                    "flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition",
                    "border-rose-300/30 bg-rose-500/10 text-rose-700 dark:text-rose-200 dark:border-rose-400/20 backdrop-blur-xl hover:bg-rose-500/15"
                  )}
                >
                  <XCircle className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t("strategies.list.loadError")}</span>
                </button>
              ) : filtered.length === 0 ? (
                <EmptyList />
              ) : (
                <ul className="space-y-2">
                  {filtered.map((s) => (
                    <li key={s.id}>
                      <StrategyItemCard
                        s={s}
                        selected={selectedId === s.id}
                        onClick={() => { setSelectedId(s.id); setEditing(null); }}
                        onEdit={(e) => { e.stopPropagation(); setEditing(s); setSelectedId(null); }}
                        onToggleFavorite={(e) => { e.stopPropagation(); toggleFavorite(s.id); }}
                        onDelete={(e) => { e.stopPropagation(); askDelete(s.id); }}
                        isFavorite={favoriteIds.includes(s.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-1">
            {fetchError ? (
              <button
                onClick={() => setShowErrorPopup(true)}
                title={t("strategies.list.viewMessage")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-500/40 bg-rose-500/15 text-rose-600 dark:text-rose-300 transition hover:bg-rose-500/25 animate-pulse"
              >
                <XCircle className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => setLibOpen(true)}
                title={t("strategies.list.expand")}
                className={cx(
                  "inline-flex h-10 w-10 items-center justify-center rounded-xl transition",
                  "border border-white/40 dark:border-white/12",
                  "bg-white/65 dark:bg-[#0f172a] backdrop-blur-2xl",
                  "text-slate-600 hover:bg-white/80 dark:text-white dark:hover:bg-slate-800"
                )}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <div className={cx(
              "mt-1 select-none text-xs font-semibold uppercase tracking-[0.16em] [writing-mode:vertical-rl] rotate-180",
              fetchError ? "text-rose-600 dark:text-rose-300" : "text-slate-500 dark:text-white/70"
            )}>
              {t("strategies.nav.pageTitle")}
            </div>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-[radial-gradient(760px_220px_at_10%_8%,rgba(139,92,246,0.14),transparent_55%),radial-gradient(720px_220px_at_90%_10%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(720px_240px_at_50%_100%,rgba(217,70,239,0.08),transparent_60%)]" />

      {isViewerFullscreen ? (
        <div className="mx-auto max-w-[1540px] px-4 py-6 md:px-8 md:py-10">
          <StrategiesHero />
          <main className={cx(UI.panel, UI.panelInset, "min-h-[760px] p-4 sm:p-5 lg:p-6")}>
            <div className={cx("pointer-events-none absolute inset-0", UI.panelGlow)} />
            <div className="relative h-full">
              <StrategyViewer
                strategy={selected}
                onEdit={() => setEditing(selected)}
                onBack={() => setSelectedId(null)}
                isFavorite={favoriteIds.includes(selected.id)}
                onToggleFavorite={() => toggleFavorite(selected.id)}
              />
            </div>
          </main>
        </div>
      ) : (
        <div className="flex flex-col items-stretch lg:flex-row">
          {/* Pas connecté : on masque la 2e sidebar (rail « Vos stratégies ») */}
          {isDesktop && !fetchError && listPanel}

          {/* Colonne droite : hero + toolbar + viewer */}
          <div className="min-w-0 flex-1 px-4 py-6 md:px-8 md:py-10">
            <div className="mx-auto max-w-[1400px]">
              {/* Bannière visible si non connecté et popup fermé */}
              {fetchError && !showErrorPopup && (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-rose-400 bg-rose-100 px-4 py-3 text-sm font-medium text-rose-800 shadow-sm dark:border-rose-400/40 dark:bg-rose-500/20 dark:text-rose-100 lg:pr-28">
                  <TriangleAlert className="h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300" />
                  <span className="min-w-0 flex-1">{fetchError}</span>
                  <button
                    onClick={() => loadStrategies()}
                    className="shrink-0 rounded-xl bg-rose-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-rose-500 active:scale-[0.98]"
                  >
                    {t("strategies.actions.retry")}
                  </button>
                </div>
              )}

              <StrategiesHero />

              {/* Toolbar horizontale */}
              <div className="mb-6 py-2">
                <div className={cx(UI.panel, "p-3 md:p-4")}>
                  <div className={cx("pointer-events-none absolute inset-0", UI.panelGlow)} />
                  <div className="relative">
                    <SidebarToolbar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      onCreate={() => { setSelectedId(null); setEditing({ ...emptyStrategy }); }}
                      sortBy={sortBy}
                      setSortBy={setSortBy}
                      sortOrder={sortOrder}
                      setSortOrder={setSortOrder}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <main className={cx(UI.panel, UI.panelInset, "min-h-[640px] p-4 sm:p-5 lg:p-6")}>
                <div className={cx("pointer-events-none absolute inset-0", UI.panelGlow)} />
                <div className="relative h-full">
                  {editing ? (
                    <StrategyEditor
                      initialStrategy={editing}
                      onCancel={() => setEditing(null)}
                      onSave={handleSave}
                      existingNames={userStrats.filter((s) => s.id !== editing.id).map((s) => s.name?.trim()).filter(Boolean)}
                      setUserStrats={setUserStrats}
                    />
                  ) : selected ? (
                    <StrategyViewer
                      strategy={selected}
                      onEdit={() => setEditing(selected)}
                      onBack={() => setSelectedId(null)}
                      isFavorite={favoriteIds.includes(selected.id)}
                      onToggleFavorite={() => toggleFavorite(selected.id)}
                    />
                  ) : (
                    <WelcomeEmpty />
                  )}
                </div>
              </main>
            </div>
          </div>
        </div>
      )}

      {!isDesktop && !fetchError && pageNavOpen && pageNavBody
        ? createPortal(listPanel, pageNavBody)
        : null}

      {showDelete && (
        <DeleteModal
          name={userStrats.find((s) => s.id === toDelete)?.name || t("strategies.deleteModal.fallbackName")}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}

      {showErrorPopup && fetchError && (
        <ErrorPopup
          message={fetchError}
          onClose={() => setShowErrorPopup(false)}
          onRetry={() => { setShowErrorPopup(false); loadStrategies(); }}
        />
      )}
    </div>
  );
}

/* ===========================
 *   TITLE BAR (sticky)
 * =========================== */
function TitleBar({ icon, title, subtitle, right, edgeBadge }) {
  return (
    <div className={cx("sticky top-0 z-[5] -mx-5 -mt-5 px-5 pt-4 pb-3 sm:-mx-6 sm:px-6", UI.sticky)}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_220px_at_10%_0%,rgba(124,58,237,0.10),transparent_55%),radial-gradient(900px_240px_at_90%_0%,rgba(14,165,233,0.08),transparent_55%)]" />
      <div className="relative flex items-start gap-3">
        <div className="text-violet-700 dark:text-violet-300">{icon}</div>
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold leading-tight truncate text-gray-900 dark:text-white">
            {title}
          </h3>
          {subtitle && <p className="text-xs sm:text-[13px] text-gray-600 dark:text-slate-300 mt-0.5">{subtitle}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {edgeBadge}
          {right}
        </div>
      </div>
    </div>
  );
}

/* ======= TopBar typée (viewer) ======= */
function StrategyTopBar({ strategy, review, isFavorite, onToggleFavorite, onEdit }) {
  const { t } = useTranslation();
  const theme = getTypeTheme(strategy?.type);
  const score100 = clamp(scoreFromReview({ last_review: review ?? strategy?.__review }), 0, 100);

  return (
    <div className={cx("sticky top-0 z-[5] -mx-5 -mt-5 px-5 pt-4 pb-3 sm:-mx-6 sm:px-6", UI.sticky)}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_220px_at_10%_0%,rgba(124,58,237,0.10),transparent_55%),radial-gradient(900px_240px_at_90%_0%,rgba(14,165,233,0.08),transparent_55%)]" />
      <div className="relative flex items-center gap-3">
        <div className={`relative shrink-0 w-9 h-9 rounded-xl ring-1 ${theme.ring} overflow-hidden`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${theme.from} ${theme.to}`} />
          <div className="relative grid place-items-center h-full text-white">
            {getStrategyTypeIcon(strategy?.type, 18)}
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base sm:text-lg font-semibold leading-tight truncate text-gray-900 dark:text-white">
              {strategy?.name || t("strategies.topBar.defaultName")}
            </h3>
            <span
              className={cx(
                "hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs",
                "border border-white/25 dark:border-white/12",
                "bg-white/45 dark:bg-[#0f172a] backdrop-blur-xl",
                "text-gray-800 dark:text-white"
              )}
            >
              {getStrategyTypeIcon(strategy?.type, 12)}
              {strategy?.type || "—"}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-gray-600 dark:text-white">{t("strategies.viewer.detailedView")}</span>
            <span className="text-[11px] text-gray-400 dark:text-slate-500">•</span>
            <Stars score={toStars5(score100)} />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1">
          <IconButton
            title={isFavorite ? t("strategies.actions.removeFavorite") : t("strategies.actions.addFavorite")}
            onClick={onToggleFavorite}
            active={isFavorite}
            activeClass="text-rose-600 bg-rose-500/10 ring-1 ring-rose-500/20"
          >
            <Heart className="w-4 h-4" />
          </IconButton>
          <IconButton title={t("strategies.actions.edit")} onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </IconButton>
        </div>
      </div>

      <div className="relative mt-3 h-1 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
        <div className={`h-1 bg-gradient-to-r ${theme.from} ${theme.to}`} style={{ width: `${score100}%` }} />
      </div>
    </div>
  );
}

/* ===========================
 *        É D I T E U R
 * =========================== */
function StrategyEditor({ initialStrategy, onCancel, onSave, existingNames, setUserStrats }) {
  const { t } = useTranslation();
  const [s, setS] = useState({ ...emptyStrategy, ...initialStrategy });
  const [formErr, setFormErr] = useState("");
  const [localReview, setLocalReview] = useState(null);
  const [running, setRunning] = useState(false);

  const pristineRef = useRef({ ...emptyStrategy, ...initialStrategy });
  const isDirty = useMemo(() => !deepEqual(pristineRef.current, s), [s]);

  useEffect(() => {
    const next = { ...emptyStrategy, ...initialStrategy };
    setS(next);
    pristineRef.current = next;
    setFormErr("");
    setLocalReview(null);
  }, [initialStrategy]);

  const setField = (name, value) => setS((prev) => ({ ...prev, [name]: value }));

  const errors = useMemo(() => {
    const errs = {};
    if (!s.name.trim()) errs.name = t("strategies.editor.validation.nameRequired");
    if (!s.type.trim()) errs.type = t("strategies.editor.validation.typeRequired");
    if (
      s.name.trim() &&
      existingNames?.includes(s.name.trim()) &&
      s.name.trim() !== (initialStrategy.name || "").trim()
    ) {
      errs.name = t("strategies.editor.validation.nameExists");
    }
    return errs;
  }, [s, existingNames, initialStrategy, t]);

  const manualAnalyze = async () => {
    if (!s.id) {
      setFormErr(t("strategies.editor.saveFirst"));
      return;
    }
    setFormErr("");
    setRunning(true);
    try {
      const resp = await apiStrategyAIReview(s.id, { save: true });
      const review = resp?.review || resp;
      setLocalReview(review);

      
      if (typeof setUserStrats === "function") {
        setUserStrats((prev) => prev.map((x) => (x.id === s.id ? { ...x, __review: review, last_review: review } : x)));
      }
    } catch (e) {
      setFormErr(t("strategies.editor.aiUnavailable"));
    } finally {
      setRunning(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      setFormErr(t("strategies.editor.validation.fixErrors"));
      return;
    }
    await onSave(s);
    setLocalReview(null);
  };

  return (
    <form onSubmit={submit} className="space-y-7">
      <TitleBar
        icon={<Edit2 className="w-5 h-5" />}
        title={s.id ? t("strategies.editor.editTitle") : t("strategies.editor.createTitle")}
        subtitle={t("strategies.editor.subtitle")}
        edgeBadge={isDirty ? (
          <span className={cx(UI.chip, "px-3 py-1", "text-amber-950 dark:text-amber-100 border-amber-300/30 dark:border-amber-400/20 bg-amber-500/10")}>
            <Sparkles className="w-3.5 h-3.5" /> {t("strategies.editor.draftBadge")}
          </span>
        ) : null}
        right={null}
      />

      {/* 1. Infos générales */}
      <Section step={1} title={t("strategies.editor.section1.title")} kicker={t("strategies.editor.section1.kicker")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("strategies.editor.fields.name")} error={errors.name}>
            <input
              value={s.name}
              onChange={(e) => setField("name", e.target.value)}
              className={cx(UI.input, errors.name ? "ring-2 ring-rose-500/40 border-rose-400/40" : "")}
              placeholder={t("strategies.editor.placeholders.name")}
            />
          </Field>

          <Field label={t("strategies.editor.fields.type")} error={errors.type}>
            <select
              value={s.type}
              onChange={(e) => setField("type", e.target.value)}
              className={cx(UI.input, errors.type ? "ring-2 ring-rose-500/40 border-rose-400/40" : "")}
            >
              <option value="">{t("strategies.editor.selectPlaceholder")}</option>
              {STRAT_TYPES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>

          <Field label={t("strategies.editor.fields.markets")}>
            <MultiSelectChips
              options={MARKETS}
              values={s.markets}
              onChange={(vals) => setField("markets", vals)}
              placeholder={t("strategies.editor.placeholders.markets")}
            />
          </Field>

          <Field label={t("strategies.editor.fields.favoriteAssets")}>
            <input
              value={s.favorite_assets}
              onChange={(e) => setField("favorite_assets", e.target.value)}
              className={UI.input}
              placeholder={t("strategies.editor.placeholders.favoriteAssets")}
            />
          </Field>
        </div>
      </Section>

      {/* 2. Timeframes */}
      <Section step={2} title={t("strategies.editor.section2.title")} kicker={t("strategies.editor.section2.kicker")}>
        <Field>
          <MultiSelectChips
            options={TIMEFRAMES}
            values={s.timeframes}
            onChange={(vals) => setField("timeframes", vals)}
            placeholder={t("strategies.editor.placeholders.timeframes")}
          />
          <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">{t("strategies.editor.timeframesHint")}</p>
        </Field>
      </Section>

      {/* 3. Règles */}
      <Section step={3} title={t("strategies.editor.section3.title")} kicker={t("strategies.editor.section3.kicker")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("strategies.editor.fields.entries")}>
            <Textarea
              value={s.entries}
              onChange={(v) => setField("entries", v)}
              rows={5}
              placeholder={t("strategies.editor.placeholders.entries")}
            />
          </Field>
          <Field label={t("strategies.editor.fields.exits")}>
            <Textarea
              value={s.exits}
              onChange={(v) => setField("exits", v)}
              rows={5}
              placeholder={t("strategies.editor.placeholders.exits")}
            />
          </Field>
        </div>
        <Field label={t("strategies.editor.fields.exceptions")}>
          <Textarea
            value={s.exceptions}
            onChange={(v) => setField("exceptions", v)}
            rows={3}
            placeholder={t("strategies.editor.placeholders.exceptions")}
          />
        </Field>
      </Section>

      {/* 4. Gestion des positions */}
      <Section step={4} title={t("strategies.editor.section4.title")} kicker={t("strategies.editor.section4.kicker")}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={t("strategies.editor.fields.takeProfit")}>
            <input
              value={s.take_profit || ""}
              onChange={(e) => setField("take_profit", e.target.value)}
              className={UI.input}
              placeholder={t("strategies.editor.placeholders.takeProfit")}
            />
          </Field>
          <Field label={t("strategies.editor.fields.stopLoss")}>
            <input
              value={s.stop_loss || ""}
              onChange={(e) => setField("stop_loss", e.target.value)}
              className={UI.input}
              placeholder={t("strategies.editor.placeholders.stopLoss")}
            />
          </Field>
          <Field label={t("strategies.editor.fields.breakEven")}>
            <select
              value={s.break_even || ""}
              onChange={(e) => setField("break_even", e.target.value)}
              className={UI.input}
            >
              <option value="">{t("strategies.editor.selectPlaceholder")}</option>
              {BREAK_EVEN.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      {/* 5. Money management */}
      <Section step={5} title={t("strategies.editor.section5.title")} kicker={t("strategies.editor.section5.kicker")}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={t("strategies.editor.fields.mmType")}>
            <SelectOneWithHelp
              value={s.mm_type || ""}
              onChange={(val) => setField("mm_type", val)}
              options={["", ...MM_TYPES]}
              definitions={MM_DEFS}
              placeholder={t("strategies.editor.selectPlaceholder")}
            />
          </Field>

          <Field label={t("strategies.editor.fields.riskPerTrade")}>
            <input
              type="number"
              step="0.1"
              min="0"
              value={s.risk_per_trade ?? ""}
              onChange={(e) => setField("risk_per_trade", e.target.value === "" ? null : Number(e.target.value))}
              className={UI.input}
              placeholder={t("strategies.editor.placeholders.riskPerTrade")}
            />
          </Field>

          <div className="hidden md:block" />
        </div>

        <Field label={t("strategies.editor.fields.mmExplain")}>
          <Textarea
            value={s.mm_explain || ""}
            onChange={(v) => setField("mm_explain", v)}
            rows={3}
            placeholder={t("strategies.editor.placeholders.mmExplain")}
          />
        </Field>
      </Section>

      {formErr && <Alert tone="danger"><XCircle className="w-5 h-5" /> {formErr}</Alert>}

      <div className="flex flex-col sm:flex-row justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onCancel}><X className="w-4 h-4" /> {t("strategies.actions.cancel")}</Button>
        <Button variant="warning" type="button" onClick={manualAnalyze} disabled={running || !s.id}>
          <Lightbulb className="w-4 h-4" /> {running ? t("strategies.editor.analyzing") : t("strategies.editor.analyzeWithAI")}
        </Button>
        <Button variant="primary" type="submit" disabled={Object.keys(errors).length > 0}>
          <Save className="w-4 h-4" /> {t("strategies.actions.save")}
        </Button>
      </div>

      {localReview && (
        <div className="pt-2"><ReviewCard review={localReview} /></div>
      )}
    </form>
  );
}

/* ===========================
 *        V I E W E R
 * =========================== */
function StrategyViewer({ strategy, isFavorite, onToggleFavorite, onEdit, onBack }) {
  const { t } = useTranslation();
  const [review, setReview] = useState(strategy.__review || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!strategy?.id) return;
      setLoading(true);
      try {
        const res = await apiGetStrategyReview(strategy.id);
        if (active) setReview(res?.review ?? null);
      } catch (e) {
        console.warn("get review failed", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [strategy?.id]);

  const score100 = clamp(scoreFromReview({ last_review: review ?? strategy?.__review }), 0, 100);
  const score10 = (score100 / 10).toFixed(1);
  const theme = getTypeTheme(strategy?.type);
  const createdLabel = formatReviewDate(review?.updated_at || review?.created_at || review?.generated_at, t);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-[26px] border border-slate-200/80 bg-white/85 p-3  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220] sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-fit items-center gap-2 rounded-2xl border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-800 transition hover:bg-violet-100 dark:border-violet-400/30 dark:bg-[#1b1633] dark:text-violet-100 dark:hover:bg-[#251d49]"
        >
          <span className="text-lg leading-none">←</span>
          {t("strategies.viewer.backToLibrary")}
        </button>

        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">
          <span>{t("strategies.viewer.detailedView")}</span>
          <span className="hidden h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-500 sm:inline-flex" />
          <span>{t("strategies.viewer.fullReading")}</span>
        </div>
      </div>

      <div className="rounded-[30px] border border-slate-200/80 bg-white/90 p-4  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220] sm:p-5">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[20px] border border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/35 dark:bg-emerald-500/10 dark:text-emerald-200">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                {t("strategies.viewer.analysisKicker")}
              </div>
              <h3 className="mt-1 text-xl font-bold tracking-tight text-slate-950 dark:text-white">
                {review ? t("strategies.viewer.aiDone") : t("strategies.viewer.noAnalysis")}
              </h3>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-200">
                {review
                  ? t("strategies.viewer.lastUpdate", { date: createdLabel })
                  : t("strategies.viewer.noAnalysisDesc")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 xl:justify-end">
            {review && (
              <div className="inline-flex items-center gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-2.5 dark:border-violet-400/30 dark:bg-[#1b1633]">
                <div className="text-right">
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-violet-700 dark:text-violet-200">{t("strategies.viewer.aiScore")}</div>
                  <div className="text-lg font-bold text-slate-950 dark:text-white">
                    {score10}<span className="text-sm font-semibold text-slate-500 dark:text-slate-300">/10</span>
                  </div>
                </div>
                <div className="h-10 w-px bg-violet-300 dark:bg-violet-500/30" />
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">{t("strategies.viewer.level")}</div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">{review?.grade || t("strategies.viewer.evaluated")}</div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-[#101827]">
              <IconButton
                title={isFavorite ? t("strategies.actions.removeFavorite") : t("strategies.actions.addFavorite")}
                onClick={onToggleFavorite}
                active={isFavorite}
                activeClass="text-rose-600 bg-rose-500/10 ring-1 ring-rose-500/20"
              >
                <Heart className="h-4 w-4" />
              </IconButton>
              <IconButton title={t("strategies.actions.edit")} onClick={onEdit}>
                <Edit2 className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(330px,410px),minmax(0,1fr)]">
        <aside className="rounded-[30px] border border-slate-200/80 bg-white/90 p-5  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220] xl:sticky xl:top-5 xl:self-start">
          <div className="flex items-start gap-4">
            <div className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-[22px] ring-1 ${theme.ring}`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${theme.from} ${theme.to}`} />
              <div className="relative grid h-full place-items-center text-white">
                {getStrategyTypeIcon(strategy?.type, 28)}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="max-w-full truncate text-[28px] font-bold tracking-tight text-slate-950 dark:text-white">
                  {strategy?.name || t("strategies.viewer.defaultName")}
                </h3>
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-800 dark:border-violet-400/30 dark:bg-[#1b1633] dark:text-violet-100">
                  {getStrategyTypeIcon(strategy?.type, 13)}
                  {strategy?.type || t("strategies.viewer.notSet")}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-200">
                <Stars score={toStars5(score100)} />
                <span>{t("strategies.viewer.scoreLabel", { score: score10 })}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-3">
            <PremiumDetail label={t("strategies.viewer.details.type")} value={strategy?.type || "—"} />
            <PremiumDetail label={t("strategies.viewer.details.targetMarkets")} value={formatArray(strategy?.markets)} />
            <PremiumDetail label={t("strategies.viewer.details.favoriteAssets")} value={strategy?.favorite_assets || "—"} />
            <PremiumDetail label={t("strategies.viewer.details.timeframes")} value={formatArray(strategy?.timeframes)} />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <InfoTag title={t("strategies.viewer.tags.takeProfit")} value={strategy?.take_profit || t("strategies.viewer.notSet")} />
            <InfoTag title={t("strategies.viewer.tags.stopLoss")} value={strategy?.stop_loss || t("strategies.viewer.notSet")} />
            <InfoTag title={t("strategies.viewer.tags.breakEven")} value={strategy?.break_even || t("strategies.viewer.notSet")} />
            <InfoTag title={t("strategies.viewer.tags.maxRisk")} value={typeof strategy?.risk_per_trade === "number" ? `${strategy.risk_per_trade}%` : t("strategies.viewer.notSet")} />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-bold text-violet-800 transition hover:bg-violet-100 dark:border-violet-400/30 dark:bg-[#1b1633] dark:text-violet-100 dark:hover:bg-[#251d49]"
            >
              {t("strategies.viewer.editStrategy")}
              <Edit2 className="h-4 w-4" />
            </button>
            {strategy?.mm_type ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-700 dark:border-slate-700 dark:bg-[#101827] dark:text-slate-200">
                {t("strategies.viewer.mmPrefix", { type: strategy.mm_type })}
                <InlineHelp text={MM_DEFS[strategy.mm_type || ""]} />
              </span>
            ) : null}
          </div>
        </aside>

        {loading ? (
          <div className="grid min-h-[520px] place-items-center rounded-[30px] border border-slate-200/80 bg-white/90 p-6  dark:border-slate-700 dark:bg-[#0b1220]">
            <div className="text-sm font-medium text-slate-600 dark:text-slate-200">{t("strategies.viewer.loadingAnalysis")}</div>
          </div>
        ) : (
          <ReviewCard review={review} score100={score100} />
        )}
      </div>

      <section className="rounded-[30px] border border-slate-200/80 bg-white/90 p-5  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220]">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-200">
              {t("strategies.viewer.rubricsKicker")}
            </div>
            <h3 className="mt-1 text-xl font-bold tracking-tight text-slate-950 dark:text-white">
              {t("strategies.viewer.rubricsTitle")}
            </h3>
          </div>
          <div className="text-sm font-medium text-slate-500 dark:text-slate-300">
            {t("strategies.viewer.rubricsHint")}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <PremiumTextCard title={t("strategies.viewer.cards.entries")} value={strategy?.entries} />
          <PremiumTextCard title={t("strategies.viewer.cards.exits")} value={strategy?.exits} />
          <PremiumTextCard title={t("strategies.viewer.cards.exceptions")} value={strategy?.exceptions} />
          <PremiumTextCard
            title={t("strategies.viewer.cards.mmExplain")}
            value={strategy?.mm_explain}
            footer={strategy?.mm_type ? t("strategies.viewer.mmFooter", { type: strategy.mm_type }) : null}
          />
        </div>
      </section>
    </div>
  );
}

function formatArray(values) {
  return Array.isArray(values) && values.length ? values.join(", ") : "—";
}

function formatReviewDate(value, t) {
  const recently = t ? t("strategies.viewer.recently") : "récemment";
  if (!value) return recently;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return recently;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function PremiumDetail({ label, value }) {
  return (
    <div className="grid gap-1.5 rounded-2xl border border-slate-200 bg-white px-4 py-3  dark:border-slate-700 dark:bg-[#101827]">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">{label}</div>
      <div className="break-words text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">{value}</div>
    </div>
  );
}


function InfoTag({ title, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3  dark:border-slate-700 dark:bg-[#101827]">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">{title}</div>
      <div className="mt-1 break-words text-sm font-bold text-slate-800 dark:text-slate-100">{value}</div>
    </div>
  );
}


function PremiumTextCard({ title, value, footer = null }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5  dark:border-slate-700 dark:bg-[#101827]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-slate-950 dark:text-white">{title}</div>
        <div className="h-2 w-2 shrink-0 rounded-full bg-violet-500" />
      </div>
      <div className="mt-3 min-h-[96px] whitespace-pre-wrap break-words text-sm leading-7 text-slate-700 dark:text-slate-200">
        {value || t("strategies.textCard.empty")}
      </div>
      {footer ? <div className="mt-4 rounded-2xl border border-violet-300 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-800 dark:border-violet-400/30 dark:bg-[#1b1633] dark:text-violet-100">{footer}</div> : null}
    </div>
  );
}


function ReviewCard({ review, score100: scoreProp }) {
  const { t } = useTranslation();
  const safeReview = review || null;
  const p = safeReview?.pillars || {};
  const score100 = typeof scoreProp === "number" ? scoreProp : clamp(safeReview?.score_overall ?? 0, 0, 100);
  const score10 = (score100 / 10).toFixed(1);
  const grade = safeReview?.grade || (score100 >= 80 ? t("strategies.reviewCard.grades.good") : score100 >= 60 ? t("strategies.reviewCard.grades.promising") : t("strategies.reviewCard.grades.needsWork"));

  const pillars = [
    { label: t("strategies.reviewCard.pillars.clarity.label"), value: Number(p.clarity?.score || 0), text: p.clarity?.comments || p.clarity?.feedback || t("strategies.reviewCard.pillars.clarity.default") },
    { label: t("strategies.reviewCard.pillars.multiTimeframe.label"), value: Number(p.multi_timeframe?.score || 0), text: p.multi_timeframe?.comments || p.multi_timeframe?.feedback || t("strategies.reviewCard.pillars.multiTimeframe.default") },
    { label: t("strategies.reviewCard.pillars.riskManagement.label"), value: Number(p.risk_management?.score || 0), text: p.risk_management?.comments || p.risk_management?.feedback || t("strategies.reviewCard.pillars.riskManagement.default") },
    { label: t("strategies.reviewCard.pillars.moneyManagement.label"), value: Number(p.money_management?.score || 0), text: p.money_management?.comments || p.money_management?.feedback || t("strategies.reviewCard.pillars.moneyManagement.default") },
    { label: t("strategies.reviewCard.pillars.exceptions.label"), value: Number(p.exceptions?.score || 0), text: p.exceptions?.comments || p.exceptions?.feedback || t("strategies.reviewCard.pillars.exceptions.default") },
  ];

  const missing = Array.isArray(safeReview?.missing_fields) ? safeReview.missing_fields : [];
  const risks = Array.isArray(safeReview?.risks) ? safeReview.risks : [];
  const contradictions = Array.isArray(safeReview?.contradictions) ? safeReview.contradictions : [];
  const suggestions = Array.isArray(safeReview?.suggestions) ? safeReview.suggestions : [];
  const riskItems = [...risks, ...contradictions].slice(0, 4);

  if (!safeReview) {
    return (
      <div className="rounded-[30px] border border-slate-200/80 bg-white/90 p-6  dark:border-slate-700 dark:bg-[#0b1220]">
        <div className="flex h-full min-h-[520px] flex-col items-center justify-center text-center">
          <div className="grid h-16 w-16 place-items-center rounded-[22px] border border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-400/30 dark:bg-violet-500/10 dark:text-violet-100">
            <Sparkles className="h-7 w-7" />
          </div>
          <h4 className="mt-4 text-xl font-bold tracking-tight text-slate-950 dark:text-white">
            {t("strategies.reviewCard.noAnalysisTitle")}
          </h4>
          <p className="mt-2 max-w-md text-sm leading-7 text-slate-600 dark:text-slate-200">
            {t("strategies.reviewCard.noAnalysisDesc")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[30px] border border-slate-200/80 bg-white/90 p-4  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220] sm:p-5">
      <div className="space-y-5">
        <div className="grid gap-5 2xl:grid-cols-[minmax(300px,380px),minmax(0,1fr)] 2xl:items-stretch">
          <div className="rounded-[30px] border border-slate-200 bg-white p-5 text-center  dark:border-slate-700 dark:bg-[#101827]">
            <div className="flex justify-center">
              <ScoreRing score={score100} />
            </div>

            <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              {t("strategies.reviewCard.globalScore")}
            </div>

            <div className="mt-2 text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
              {grade}
            </div>

            <p className="mx-auto mt-3 max-w-[520px] whitespace-pre-wrap text-sm leading-7 text-slate-700 dark:text-slate-200">
              {safeReview.summary || t("strategies.reviewCard.summaryDefault")}
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs">
              <PremiumBadge tone={score100 >= 80 ? "success" : score100 >= 60 ? "warning" : "danger"}>
                {t("strategies.reviewCard.overallNote", { score: score10 })}
              </PremiumBadge>
              <PremiumBadge>{safeReview.grade || t("strategies.reviewCard.aiEvaluation")}</PremiumBadge>
            </div>
          </div>

          <div className="rounded-[30px] border border-slate-200 bg-white p-5  dark:border-slate-700 dark:bg-[#101827]">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-200">
                  {t("strategies.reviewCard.pillarsKicker")}
                </div>
                <h4 className="mt-1 text-lg font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("strategies.reviewCard.pillarsTitle")}
                </h4>
              </div>
              <div className="text-sm font-medium text-slate-500 dark:text-slate-300">
                {t("strategies.reviewCard.pillarsHint")}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {pillars.map((item) => (
                <AnalysisPillar key={item.label} label={item.label} value={item.value} text={item.text} />
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <InsightCard
            title={t("strategies.reviewCard.insights.missing.title")}
            items={missing}
            emptyText={t("strategies.reviewCard.insights.missing.empty")}
            tone="warning"
            cta={t("strategies.reviewCard.insights.missing.cta")}
          />
          <InsightCard
            title={t("strategies.reviewCard.insights.risks.title")}
            items={riskItems}
            emptyText={t("strategies.reviewCard.insights.risks.empty")}
            tone="danger"
            cta={t("strategies.reviewCard.insights.risks.cta")}
          />
          <InsightCard
            title={t("strategies.reviewCard.insights.suggestions.title")}
            items={suggestions}
            emptyText={t("strategies.reviewCard.insights.suggestions.empty")}
            tone="info"
            cta={t("strategies.reviewCard.insights.suggestions.cta")}
          />
        </div>
      </div>
    </div>
  );
}

function ScoreRing({ score }) {
  const pct = clamp(score, 0, 100);
  const deg = Math.round((pct / 100) * 360);
  const score10 = (pct / 10).toFixed(1);
  return (
    <div
      className="relative grid h-[118px] w-[118px] shrink-0 place-items-center rounded-full"
      style={{
        background: `conic-gradient(rgb(16 185 129) 0deg, rgb(59 130 246) ${Math.max(60, deg * 0.45)}deg, rgb(139 92 246) ${deg}deg, rgba(226,232,240,0.9) ${deg}deg 360deg)`,
      }}
    >
      <div className="grid h-[92px] w-[92px] place-items-center rounded-full border border-white/60 dark:border-white/12 bg-white/95 dark:bg-[#0b1020] shadow-inner">
        <div className="text-center">
          <div className="text-[42px] font-semibold tracking-tight text-slate-950 dark:text-white">{score10}</div>
          <div className="-mt-1 text-sm text-slate-500 dark:text-white">/10</div>
        </div>
      </div>
    </div>
  );
}

function AnalysisPillar({ label, value, text }) {
  const pct = Math.max(0, Math.min(100, (Number(value) || 0) / 5 * 100));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4  dark:border-slate-700 dark:bg-[#0f172a]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-slate-950 dark:text-slate-100">{label}</div>
        <div className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200">{value.toFixed(1)}/5</div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-3 text-xs leading-6 text-slate-600 dark:text-slate-200">{text}</p>
    </div>
  );
}


function InsightCard({ title, items = [], emptyText, tone = "info", cta }) {
  const toneMap = {
    info: {
      wrap: "border-sky-200 bg-sky-50 dark:border-sky-400/30 dark:bg-[#102235]",
      dot: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-100",
      cta: "text-sky-700 dark:text-sky-100",
      bullet: "bg-sky-500",
    },
    warning: {
      wrap: "border-amber-200 bg-amber-50 dark:border-amber-400/30 dark:bg-[#2a2211]",
      dot: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100",
      cta: "text-amber-700 dark:text-amber-100",
      bullet: "bg-amber-500",
    },
    danger: {
      wrap: "border-rose-200 bg-rose-50 dark:border-rose-400/30 dark:bg-[#311923]",
      dot: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-100",
      cta: "text-rose-700 dark:text-rose-100",
      bullet: "bg-rose-500",
    },
  };
  const current = toneMap[tone] || toneMap.info;

  return (
    <div className={cx("rounded-[24px] border p-5 ", current.wrap)}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-lg font-bold tracking-tight text-slate-950 dark:text-white">{title}</div>
        <div className={cx("grid h-9 w-9 place-items-center rounded-full text-sm font-bold", current.dot)}>
          {items.length}
        </div>
      </div>

      {items.length ? (
        <ul className="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-100">
          {items.slice(0, 4).map((item, idx) => (
            <li key={`${title}-${idx}`} className="flex gap-2">
              <span className={cx("mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full", current.bullet)} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-200">{emptyText}</p>
      )}

      <div className={cx("mt-5 inline-flex items-center gap-2 text-sm font-bold", current.cta)}>
        <span>{cta}</span>
        <ArrowUpRight className="h-4 w-4" />
      </div>
    </div>
  );
}


function PremiumBadge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-[#101827] dark:text-slate-100",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/30 dark:bg-[#11261f] dark:text-emerald-100",
    warning: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-[#2a2211] dark:text-amber-100",
    danger: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/30 dark:bg-[#311923] dark:text-rose-100",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full border px-3 py-1.5 font-bold", tones[tone] || tones.neutral)}>
      {children}
    </span>
  );
}


function Button({ children, variant = "primary", disabled, ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm transition " +
    "active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-violet-500/50 " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  const styles = {
    primary:
      "text-white bg-gradient-to-b from-violet-500 to-violet-700 hover:from-violet-400 hover:to-violet-700 " +
      " ring-1 ring-white/10",
    warning:
      "text-amber-950 bg-gradient-to-b from-amber-200/90 to-amber-100/70 hover:from-amber-200 hover:to-amber-100 " +
      "ring-1 ring-amber-300/35  " +
      "dark:text-amber-100 dark:from-amber-500/20 dark:to-amber-500/10 dark:ring-amber-400/20",
    ghost:
      "text-gray-900 dark:text-white bg-white/45 dark:bg-[#101827] hover:bg-white/65 dark:hover:bg-slate-800 " +
      "border border-white/30 dark:border-white/12 backdrop-blur-xl",
    danger:
      "text-white bg-gradient-to-b from-rose-500 to-rose-700 hover:from-rose-400 hover:to-rose-700 " +
      " ring-1 ring-white/10",
  };

  return (
    <button className={cx(base, styles[variant])} disabled={disabled} {...rest}>
      {children}
    </button>
  );
}

function IconButton({ children, onClick, title, active, activeClass = "", danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={cx(
        "p-2 rounded-xl transition focus:outline-none focus:ring-2 focus:ring-violet-500/50 backdrop-blur-xl",
        "border border-transparent",
        danger
          ? "text-rose-600 dark:text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/15"
          : "text-gray-600 dark:text-white hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-500/10 hover:border-violet-500/15",
        active ? activeClass : ""
      )}
    >
      {children}
    </button>
  );
}

function Alert({ children, tone = "info" }) {
  const map = {
    info: "border-sky-300/30 bg-sky-500/10 text-sky-950 dark:text-sky-100 dark:border-sky-400/20",
    warning: "border-amber-300/30 bg-amber-500/10 text-amber-950 dark:text-amber-100 dark:border-amber-400/20",
    danger: "border-rose-300/30 bg-rose-500/10 text-rose-950 dark:text-rose-100 dark:border-rose-400/20",
    success: "border-emerald-300/30 bg-emerald-500/10 text-emerald-950 dark:text-emerald-100 dark:border-emerald-400/20",
  };
  return (
    <div className={cx("flex items-start gap-2 rounded-xl border p-3 backdrop-blur-2xl", map[tone])}>
      {children}
    </div>
  );
}

/* Section avec step & kicker */
function Section({ step, title, kicker, children }) {
  return (
    <section className="relative">
      <div className="mb-3 flex items-start gap-3">
        {typeof step === "number" && (
          <span className="mt-0.5 inline-grid h-8 w-8 shrink-0 place-items-center rounded-2xl border border-violet-200 bg-violet-50 text-sm font-bold text-violet-800 dark:border-violet-400/35 dark:bg-[#1b1633] dark:text-violet-100">
            {step}
          </span>
        )}
        <div className="min-w-0">
          <h4 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">{title}</h4>
          {kicker && <div className="mt-0.5 text-sm font-medium text-gray-600 dark:text-slate-300">{kicker}</div>}
        </div>
      </div>

      <div className="rounded-[26px] border border-slate-200/90 bg-white/90 p-4  backdrop-blur-2xl dark:border-slate-700 dark:bg-[#0b1220] dark: sm:p-5">
        <div className="grid gap-4">{children}</div>
      </div>
    </section>
  );
}

function Field({ label, children, error }) {
  return (
    <label className="block rounded-[20px] border border-slate-200/70 bg-white/75 p-3  dark:border-slate-700/80 dark:bg-[#101827]">
      {label && <div className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300">{label}</div>}
      {children}
      {error && (
        <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-rose-700 dark:text-rose-200">
          <XCircle className="w-4 h-4" /> {error}
        </div>
      )}
    </label>
  );
}

function Textarea({ value, onChange, rows = 4, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={cx(UI.input, "min-h-[44px]")}
      placeholder={placeholder}
    />
  );
}

function ViewerRow({ label, children, wide = false }) {
  return (
    <div className={cx(wide ? "md:col-span-2" : "")}>
      <div className="text-xs font-medium text-gray-600 dark:text-white mb-1">{label}</div>
      <div className="text-sm text-gray-900 dark:text-slate-100">{children}</div>
    </div>
  );
}

function ScrollableText({ children }) {
  return (
    <div className={cx(
      "max-h-40 overflow-auto rounded-xl p-3 whitespace-pre-wrap text-sm",
      "border border-white/25 dark:border-white/12",
      "bg-white/55 dark:bg-[#101827] backdrop-blur-2xl"
    )}>
      <span className="text-gray-800 dark:text-slate-100">{children}</span>
    </div>
  );
}

/* ===========================
 * SidebarToolbar / Segmented
 * =========================== */
function SidebarToolbar({ searchTerm, setSearchTerm, onCreate, sortBy, setSortBy, sortOrder, setSortOrder, loading }) {
  const { t } = useTranslation();
  const handleSortChange = (key) => {
    if (sortBy === key) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else setSortBy(key);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="mr-1 shrink-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-white">{t("strategies.toolbar.libraryKicker")}</div>
        <div className="text-lg font-semibold tracking-tight text-slate-950 dark:text-white leading-tight">{t("strategies.toolbar.libraryTitle")}</div>
      </div>

      <div className="relative min-w-[200px] flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-white" />
        <input
          className={cx(UI.input, "h-12 rounded-2xl pl-10 pr-4")}
          placeholder={t("strategies.toolbar.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Segmented
        value={sortBy}
        onChange={handleSortChange}
        options={[
          { value: "name", label: t("strategies.toolbar.sort.name") },
          { value: "score", label: t("strategies.toolbar.sort.score") },
          { value: "type", label: t("strategies.toolbar.sort.type") },
        ]}
      />

      <button
        onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
        className={cx(
          "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition",
          "border border-white/40 dark:border-white/12",
          "bg-white/65 dark:bg-[#0f172a] backdrop-blur-2xl",
          "text-slate-600 hover:bg-white/80 dark:text-white dark:hover:bg-slate-800"
        )}
        title={sortOrder === "asc" ? t("strategies.toolbar.sortAsc") : t("strategies.toolbar.sortDesc")}
      >
        {sortOrder === "asc" ? <SortAsc className="h-5 w-5" /> : <SortDesc className="h-5 w-5" />}
      </button>

      <button
        onClick={onCreate}
        disabled={loading}
        className="inline-flex h-12 shrink-0 items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 px-4 text-sm font-semibold text-white  transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
        title={t("strategies.toolbar.createTitle")}
      >
        <PlusCircle className="h-4 w-4" />
        <span className="hidden sm:inline">{t("strategies.toolbar.create")}</span>
      </button>

      <div className="ml-auto shrink-0 rounded-full border border-white/45 dark:border-white/12 bg-white/65 dark:bg-[#0f172a] px-3 py-1 text-xs font-medium text-slate-600 dark:text-white backdrop-blur-xl">
        {t("strategies.toolbar.aiHub")}
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  const onKeyDown = (e) => {
    const idx = options.findIndex((o) => o.value === value);
    if (idx === -1) return;
    if (e.key === "ArrowRight") {
      onChange(options[(idx + 1) % options.length].value);
      e.preventDefault();
    } else if (e.key === "ArrowLeft") {
      onChange(options[(idx - 1 + options.length) % options.length].value);
      e.preventDefault();
    }
  };

  return (
    <div
      role="radiogroup"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={cx(
        "inline-flex rounded-xl p-1",
        "border border-white/30 dark:border-white/12",
        "bg-white/55 dark:bg-[#0f172a] backdrop-blur-2xl"
      )}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <label
            key={opt.value}
            className={cx(
              "relative cursor-pointer px-3 py-1.5 text-sm rounded-lg transition select-none",
              active
                ? "bg-white/75 dark:bg-slate-800 border border-white/35 dark:border-white/12  text-gray-900 dark:text-white"
                : "text-gray-700 dark:text-white hover:bg-white/55 dark:hover:bg-slate-800"
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name="segmented-sort"
              checked={active}
              onChange={() => onChange(opt.value)}
              aria-checked={active}
              role="radio"
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

function StrategyItemCard({ s, selected, onClick, onEdit, onToggleFavorite, onDelete, isFavorite }) {
  const { t } = useTranslation();
  const stars = toStars5(scoreFromReview(s));
  const score100 = clamp(scoreFromReview(s), 0, 100);
  const score10 = score100 ? (score100 / 10).toFixed(1) : "—";
  const theme = getTypeTheme(s?.type);

  return (
    <div
      onClick={onClick}
      className={cx(
        "group relative cursor-pointer overflow-hidden rounded-[24px] border p-4 transition-all duration-200 backdrop-blur-2xl",
        selected
          ? "border-violet-500/35 bg-violet-500/10 "
          : "border-white/40 dark:border-white/12 bg-white/60 dark:bg-[#101827]  hover:border-violet-500/20 hover:bg-white/75 dark:hover:bg-slate-800 "
      )}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(680px_220px_at_5%_0%,rgba(124,58,237,0.10),transparent_55%),radial-gradient(680px_220px_at_95%_0%,rgba(59,130,246,0.08),transparent_55%)]" />

      <div className="relative flex items-start gap-3">
        <div className={`relative mt-0.5 h-12 w-12 shrink-0 overflow-hidden rounded-2xl ring-1 ${theme.ring}`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${theme.from} ${theme.to}`} />
          <div className="relative grid h-full place-items-center text-white">
            {getStrategyTypeIcon(s?.type, 20)}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold tracking-tight text-slate-950 dark:text-white">
                {s.name || t("strategies.itemCard.untitled")}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/20 bg-violet-500/10 px-2.5 py-1 text-xs font-medium text-violet-800 dark:text-violet-100">
                  {s.type || t("strategies.itemCard.typeUnknown")}
                </span>
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-white">
                  <Stars score={stars} />
                  <span>{score10 !== "—" ? `${score10}/10` : t("strategies.itemCard.notRated")}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <IconButton
                title={isFavorite ? t("strategies.actions.removeFavorite") : t("strategies.actions.addFavorite")}
                onClick={onToggleFavorite}
                active={isFavorite}
                activeClass="text-rose-600 bg-rose-500/10 ring-1 ring-rose-500/20"
              >
                <Heart className="h-4 w-4" />
              </IconButton>
              <IconButton title={t("strategies.actions.edit")} onClick={onEdit}>
                <Edit2 className="h-4 w-4" />
              </IconButton>
              <IconButton title={t("strategies.actions.delete")} onClick={onDelete} danger>
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </div>
          </div>

          <div className="mt-3 truncate text-sm text-slate-600 dark:text-slate-200">
            {Array.isArray(s.markets) && s.markets.length > 0 ? s.markets.join(", ") : (s.favorite_assets || t("strategies.itemCard.noMarket"))}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="truncate text-xs uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
              {s.favorite_assets || t("strategies.itemCard.setupPending")}
            </div>
            <div className="inline-flex items-center gap-1 text-sm font-medium text-violet-700 dark:text-violet-200">
              <span>{t("strategies.itemCard.open")}</span>
              <ArrowUpRight className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===========================
 * Multi select chips (portal)
 * =========================== */
function MultiSelectChips({ options, values = [], onChange, placeholder = "Sélectionner…" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 0 });
  const [placeAbove, setPlaceAbove] = useState(false);

  const filtered = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(query.toLowerCase())),
    [options, query]
  );

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ left: r.left + window.scrollX, top: r.bottom + window.scrollY, width: r.width });
    requestAnimationFrame(() => {
      if (!panelRef.current) return;
      const pr = panelRef.current.getBoundingClientRect();
      const overflowBottom = pr.bottom > window.innerHeight - 8;
      setPlaceAbove(overflowBottom && r.top > pr.height + 16);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = (e) => (e.key === "Escape" ? setOpen(false) : null);

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, updatePosition]);

  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(options);

  return (
    <div className="relative">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className={cx(UI.input, "min-h-[42px] text-left flex items-center gap-2")}
      >
        {values.length === 0 ? (
          <span className="text-gray-600 dark:text-slate-300">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {values.map((v) => (
              <span
                key={v}
                className={cx(UI.chip, "text-violet-800 dark:text-violet-100")}
                onClick={(e) => e.stopPropagation()}
              >
                {v}
                <button type="button" className="opacity-70 hover:opacity-100" onClick={() => toggle(v)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <ChevronDown className="w-4 h-4 ml-auto opacity-80 text-gray-700 dark:text-slate-200" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9998]"
          onMouseDown={(e) => {
            if (
              panelRef.current &&
              !panelRef.current.contains(e.target) &&
              triggerRef.current &&
              !triggerRef.current.contains(e.target)
            ) setOpen(false);
          }}
        >
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              left: coords.left,
              top: placeAbove ? coords.top - 8 : coords.top + 8,
              width: coords.width,
              transform: placeAbove ? "translateY(-100%)" : "none",
            }}
            className={cx(UI.popover, "z-[9999]")}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={cx("p-2 border-b flex items-center gap-2", "border-white/20 dark:border-white/12")}>
              <Search className="w-4 h-4 text-gray-500 dark:text-white" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("strategies.select.filter")}
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400"
              />
              <button onClick={selectAll} className={cx("text-xs px-2 py-1 rounded-lg", UI.chip, "text-gray-800 dark:text-slate-100")}>{t("strategies.select.all")}</button>
              <button onClick={clearAll} className={cx("text-xs px-2 py-1 rounded-lg", UI.chip, "text-gray-800 dark:text-slate-100")}>{t("strategies.select.none")}</button>
            </div>

            <ul className="max-h-72 overflow-auto py-1">
              {filtered.map((opt) => {
                const isSel = values.includes(opt);
                return (
                  <li key={opt}>
                    <button
                      type="button"
                      onClick={() => toggle(opt)}
                      className={cx(
                        "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition",
                        "hover:bg-violet-50 dark:hover:bg-violet-500/10",
                        isSel ? "text-violet-800 dark:text-violet-100" : "text-gray-900 dark:text-white"
                      )}
                    >
                      <span
                        className={cx(
                          "inline-grid place-items-center w-4 h-4 rounded border",
                          isSel ? "bg-violet-600 border-violet-600 text-white" : "border-black/20 dark:border-white/20 text-transparent"
                        )}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      {opt}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-600 dark:text-white">{t("strategies.select.noResults")}</li>
              )}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ===========================
 * Select one with help (portal)
 * =========================== */
function SelectOneWithHelp({
  options = [],
  value = "",
  onChange,
  placeholder = "Sélectionner…",
  definitions = {},
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 0 });
  const [placeAbove, setPlaceAbove] = useState(false);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ left: r.left + window.scrollX, top: r.bottom + window.scrollY, width: r.width });
    requestAnimationFrame(() => {
      if (!panelRef.current) return;
      const pr = panelRef.current.getBoundingClientRect();
      const overflowBottom = pr.bottom > window.innerHeight - 8;
      setPlaceAbove(overflowBottom && r.top > pr.height + 16);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = (e) => (e.key === "Escape" ? setOpen(false) : null);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, updatePosition]);

  const currentLabel = value || "";

  return (
    <div className="relative">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className={cx(UI.input, "min-h-[42px] text-left flex items-center gap-2")}
      >
        {currentLabel ? (
          <span className="truncate">{currentLabel}</span>
        ) : (
          <span className="text-gray-600 dark:text-slate-300">{placeholder}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {currentLabel && <InlineHelp text={definitions[currentLabel]} />}
          <ChevronDown className="w-4 h-4 opacity-80 text-gray-700 dark:text-slate-200" />
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9998]"
          onMouseDown={(e) => {
            if (
              panelRef.current &&
              !panelRef.current.contains(e.target) &&
              triggerRef.current &&
              !triggerRef.current.contains(e.target)
            ) setOpen(false);
          }}
        >
          <div
            ref={panelRef}
            style={{
              position: "absolute",
              left: coords.left,
              top: placeAbove ? coords.top - 8 : coords.top + 8,
              width: coords.width,
              transform: placeAbove ? "translateY(-100%)" : "none",
            }}
            className={cx(UI.popover, "z-[9999]")}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={cx("p-2 border-b flex items-center gap-2", "border-white/20 dark:border-white/12")}>
              <Search className="w-4 h-4 text-gray-500 dark:text-white" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("strategies.select.filter")}
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-slate-400"
              />
            </div>

            <ul className="max-h-64 overflow-auto py-1">
              {filtered.map((opt) => {
                const isSel = value === opt;
                const isHover = hovered === opt;
                return (
                  <li key={opt}>
                    <button
                      type="button"
                      onMouseEnter={() => setHovered(opt)}
                      onFocus={() => setHovered(opt)}
                      onClick={() => { onChange(opt); setOpen(false); }}
                      className={cx(
                        "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition",
                        "hover:bg-violet-50 dark:hover:bg-violet-500/10",
                        isSel ? "text-violet-800 dark:text-violet-100" : "text-gray-900 dark:text-white"
                      )}
                    >
                      <span
                        className={cx(
                          "inline-grid place-items-center w-4 h-4 rounded border",
                          isSel ? "bg-violet-600 border-violet-600 text-white" : "border-black/20 dark:border-white/20 text-transparent"
                        )}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      <span className="truncate">{opt}</span>
                    </button>

                    {isHover && definitions[opt] && (
                      <div className="px-3 pb-2">
                        <div className={cx("text-xs p-2 leading-5", UI.tooltip)}>
                          {definitions[opt]}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}

              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-600 dark:text-white">{t("strategies.select.noResults")}</li>
              )}
            </ul>

            <div className={cx("border-t p-2 text-[11px]", "border-white/20 dark:border-white/12", "text-gray-600 dark:text-white")}>
              {t("strategies.select.helpHint")}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function InlineHelp({ text }) {
  if (!text) return null;
  return (
    <span className="relative group inline-flex items-center">
      <HelpCircle className="w-4 h-4 text-gray-500 dark:text-slate-400 group-hover:text-violet-700 dark:group-hover:text-violet-300" />
      <span className={cx(
        "pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-64 -translate-x-1/2 whitespace-normal p-2 text-[11px] leading-5 group-hover:block",
        UI.tooltip
      )}>
        {text}
      </span>
    </span>
  );
}

/* ===========================
 * États vides 
 * =========================== */
function EmptyList() {
  const { t } = useTranslation();
  return (
    <div className="text-center text-gray-800 dark:text-white py-10">
      <div className={cx("mx-auto w-14 h-14 rounded-2xl grid place-items-center mb-3", "bg-violet-500/10 border border-violet-500/20 backdrop-blur-xl")}>
        <BookOpen className="w-7 h-7 text-violet-800 dark:text-violet-100" />
      </div>
      <div className="font-semibold">{t("strategies.emptyList.title")}</div>
      <div className="text-sm opacity-80">{t("strategies.emptyList.desc")}</div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className={cx("rounded-2xl border p-4 animate-pulse", "border-white/25 dark:border-white/12", "bg-white/45 dark:bg-[#101827] backdrop-blur-2xl")}>
          <div className="h-4 bg-black/10 dark:bg-white/10 rounded w-2/3" />
          <div className="mt-2 h-3 bg-black/10 dark:bg-white/10 rounded w-1/3" />
        </li>
      ))}
    </ul>
  );
}

function WelcomeEmpty() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center py-16 text-center text-slate-900 dark:text-white">
      <div className="pointer-events-none absolute inset-x-0 top-10 -z-10 h-64 bg-[radial-gradient(420px_160px_at_50%_0%,rgba(124,58,237,0.12),transparent_60%)]" />
      <div className="relative mb-5">
        <div className="grid h-20 w-20 place-items-center rounded-[28px] border border-violet-500/20 bg-violet-500/10 text-violet-700  backdrop-blur-2xl dark:text-violet-200">
          <Layers className="h-9 w-9" />
        </div>
        <div className="pointer-events-none absolute -inset-3 rounded-[36px] bg-violet-500/10 blur-2xl" />
      </div>
      <h3 className="text-2xl font-semibold tracking-tight md:text-[30px]">
        {t("strategies.welcome.title")}
      </h3>
      <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 dark:text-white md:text-base">
        {t("strategies.welcome.desc")}
      </p>
      <div className="mt-8 grid w-full max-w-3xl gap-3 md:grid-cols-3">
        {[
          [t("strategies.welcome.cards.structure.title"), t("strategies.welcome.cards.structure.text")],
          [t("strategies.welcome.cards.ai.title"), t("strategies.welcome.cards.ai.text")],
          [t("strategies.welcome.cards.actions.title"), t("strategies.welcome.cards.actions.text")],
        ].map(([title, text]) => (
          <div key={title} className="rounded-[24px] border border-white/45 dark:border-white/12 bg-white/65 dark:bg-[#101827] p-4 backdrop-blur-2xl ">
            <div className="text-base font-semibold tracking-tight text-slate-950 dark:text-white">{title}</div>
            <div className="mt-2 text-sm leading-6 text-slate-600 dark:text-white">{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===========================
 * Modale 
 * =========================== */
function ErrorPopup({ message, onClose, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={cx("relative pointer-events-auto w-full max-w-sm p-4 shadow-2xl", UI.modal)}>
        <div>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-500/15 text-rose-600 dark:text-rose-300">
              <XCircle className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{t("strategies.errorPopup.title")}</div>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">{message}</p>
          <div className="mt-3 flex gap-2">
              <button
                onClick={onRetry}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-rose-500 to-rose-700 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/10 transition hover:from-rose-400 hover:to-rose-700 active:scale-[0.98]"
              >
                {t("strategies.errorPopup.retry")}
              </button>
              <button
                onClick={onClose}
                className={cx(
                  "flex-1 text-center rounded-xl px-3 py-1.5 text-xs font-medium transition active:scale-[0.98]",
                  "border border-white/30 dark:border-white/12",
                  "bg-white/55 dark:bg-[#0f172a] backdrop-blur-2xl",
                  "text-gray-800 dark:text-white hover:bg-white/70 dark:hover:bg-slate-800"
                )}
              >
                {t("strategies.errorPopup.close")}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
}

function DeleteModal({ name, onCancel, onConfirm }) {
  const { t } = useTranslation();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={onCancel}>
      <div className={cx("w-full max-w-md p-5", UI.modal)} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between pb-3 border-b border-white/20 dark:border-white/12">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{t("strategies.deleteModal.title")}</h3>
          <button
            onClick={onCancel}
            className={cx(
              "p-2 rounded-xl transition backdrop-blur-xl",
              "border border-transparent hover:border-rose-500/15 hover:bg-rose-500/10",
              "text-gray-600 dark:text-white hover:text-rose-600 dark:hover:text-rose-300"
            )}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="py-4 text-sm text-gray-700 dark:text-white">
          {t("strategies.deleteModal.confirmBefore")}{" "}
          <span className="font-semibold text-violet-800 dark:text-violet-100">{name}</span> {t("strategies.deleteModal.confirmAfter")}
          <div className={cx("rounded-xl border p-2 mt-3", "border-amber-300/25 bg-amber-500/10 text-amber-950 dark:text-amber-100 dark:border-amber-400/20 backdrop-blur-xl")}>
            {t("strategies.deleteModal.irreversibleBefore")}<strong>{t("strategies.deleteModal.irreversibleWord")}</strong>{t("strategies.deleteModal.irreversibleAfter")}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className={cx(
              "px-4 py-2 rounded-xl text-sm transition active:scale-[0.98]",
              "border border-white/30 dark:border-white/12",
              "bg-white/55 dark:bg-[#0f172a] backdrop-blur-2xl",
              "text-gray-900 dark:text-white hover:bg-white/70 dark:hover:bg-slate-800"
            )}
          >
            {t("strategies.actions.cancel")}
          </button>

          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-gradient-to-b from-rose-500 to-rose-700 text-white text-sm hover:from-rose-400 hover:to-rose-700 inline-flex items-center gap-2  ring-1 ring-white/10 active:scale-[0.98]"
          >
            <Trash2 className="w-4 h-4" /> {t("strategies.actions.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}
